import { HoldingClass, Operation, Society } from 'types';

import operationTypes from 'constants/operationTypes';

import { getTransactionAmount } from 'modules/transactions/pages/OperationDetails/utils/getTransactionAmount';
import { getTransactionSharesCount } from 'modules/transactions/utils/getTransactionSharesCount';
import { getHoldingClassFromTransaction } from 'modules/transactions/utils/getHoldingClassFromTransaction';
import { getTransactionNominalValue } from 'modules/transactions/utils/getTransactionNominalValue';

import { RangeByClass } from 'types/Partner';
import { OperationData } from '../types/OperationData';

import { getPartnerTransactionsFromOperation } from './getPartnerTransactionsFromOperation';
import { emptyOperationData } from '../constants/emptyOperationData';
import { RenumerationData } from '../types/RenumerationData';

const getRangeShares = (range: RangeByClass): number =>
  range.to - range.from + 1;

const getClassName = (value: string | HoldingClass | undefined): string =>
  (value as HoldingClass)?.name || '';

const ALWAYS_EXPAND_TYPES: string[] = [
  operationTypes.SELL_PARTICIPATION,
  operationTypes.HERITAGE,
  operationTypes.DONATION,
  operationTypes.TRANSMISSION,
  operationTypes.ENDORSEMENT,
];

const NEVER_EXPAND_TYPES: string[] = [
  operationTypes.SPLIT,
  operationTypes.RECLASSIFICATION,
  operationTypes.INFORMATIONAL,
];

const buildBaseResult = (operation: Operation): Partial<OperationData> => ({
  id: operation._id,
  alias: operation?.alias || '',
  type: operation.operationType || '',
  date:
    operation.date instanceof Date
      ? operation.date.toISOString()
      : operation.date || '',
  comments: operation.comments || '',
});

export const buildRenumerationResult = (
  operation: Operation,
  renumerationData?: RenumerationData
): OperationData => {
  const ranges = renumerationData?.rangeByClass ?? [];
  const isSingleRange = ranges.length === 1;
  const single = isSingleRange ? ranges[0] : null;

  const sharesCount = ranges.reduce((acc, r) => acc + getRangeShares(r), 0);

  return {
    ...emptyOperationData,
    ...buildBaseResult(operation),
    sharesCount,
    className: single && single.class ? getClassName(single.class) : '',
    from: single?.from || 0,
    to: single?.to || 0,
    showExpandButton: ranges.length > 1,
  };
};

export const buildTransactionResult = (
  operation: Operation,
  partnerId: string,
  society: Society
): OperationData => {
  const partnerTransactions = getPartnerTransactionsFromOperation({
    operation,
    partnerId,
  });

  const sharesCount = partnerTransactions.reduce(
    (acc, t) => acc + getTransactionSharesCount(t),
    0
  );

  const amount = partnerTransactions.reduce((acc, t) => {
    const transactionShares = getTransactionSharesCount(t);

    const nominalValue = getTransactionNominalValue({
      holdingClass: getHoldingClassFromTransaction(t),
      transaction: t,
      operation,
      society,
    });

    return (
      acc +
      getTransactionAmount({
        transaction: t,
        sharesCount: transactionShares,
        nominalValue,
      })
    );
  }, 0);

  const hasMultiple = partnerTransactions.length > 1;
  const mustExpand =
    ALWAYS_EXPAND_TYPES.includes(operation.operationType) || hasMultiple;
  const cannotExpand = NEVER_EXPAND_TYPES.includes(operation.operationType);

  const isSingleInline = !mustExpand && partnerTransactions.length === 1;
  const single = isSingleInline ? partnerTransactions[0] : null;

  return {
    ...emptyOperationData,
    ...buildBaseResult(operation),
    amount,
    sharesCount,
    className:
      single && single.shareClass ? getClassName(single.shareClass) : '',
    from: single?.shareFrom || 0,
    to: single?.shareTo || 0,
    premium: single?.sharePremium || 0,
    partnerTransactions,
    showExpandButton: mustExpand && !cannotExpand,
  };
};
